import { zodResolver } from '@hookform/resolvers/zod';
import { BeneficiaryCountry } from '@seapoint/server/enums';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '../../../../design-system/button/button';
import { Form } from '../../../../design-system/form/form-primitives';
import { Stack } from '../../../../design-system/stack/stack';
import { PaymentInputField } from '../../components/payment-input-field';
import { AddressRequiredDisclaimer } from './address-required-disclaimer';
import { CountryNameCard } from './country-name-card';

const formSchema = z.object({
  line1: z
    .string({
      required_error: 'Address is required',
    })
    .trim()
    .min(1, { message: 'Address is required' }),
  line2: z.string().optional(),
  postTown: z
    .string({
      required_error: 'City is required',
    })
    .trim()
    .min(1, { message: 'City is required' }),
  countrySubDivision: z
    .string({
      required_error: 'Region is required',
    })
    .min(1, { message: 'Region is required' }),
  postCode: z.string({ required_error: 'Postcode is required' }).min(1, { message: 'Postcode is required' }),
});

export type AddressBeneficiaryFormValues = z.infer<typeof formSchema>;

type AddressBeneficiaryFormProps = {
  onSubmit: (addressFormValues: AddressBeneficiaryFormValues) => void;
  isLoading: boolean;
  country: BeneficiaryCountry;
};

export const AddressBeneficiaryForm = ({ onSubmit, isLoading, country }: AddressBeneficiaryFormProps) => {
  const form = useForm<AddressBeneficiaryFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      line1: undefined,
      line2: undefined,
      postTown: undefined,
      countrySubDivision: undefined,
      postCode: undefined,
    },
    mode: 'onTouched',
  });

  return (
    <Stack space={3} align="stretch">
      <AddressRequiredDisclaimer />

      <CountryNameCard countryCode={country} />

      <Form {...form}>
        <form onSubmit={form.handleSubmit((addressFormValues) => onSubmit(addressFormValues))}>
          <Stack space={3} align="stretch">
            <PaymentInputField
              form={form}
              label="Recipient's Address Line 1"
              inputName="line1"
              placeholder="Recipient's Address Line 1"
            />

            <PaymentInputField
              form={form}
              label="Recipient's Address Line 2"
              inputName="line2"
              placeholder="Recipient's Address Line 2"
              isOptional
            />

            <PaymentInputField form={form} label="City" inputName="postTown" placeholder="City" />

            <div className="grid w-full grid-cols-1 gap-3 md:grid-cols-2">
              <PaymentInputField form={form} label="Region" inputName="countrySubDivision" placeholder="Region" />

              <PaymentInputField form={form} label="Postcode" inputName="postCode" placeholder="Postcode" />
            </div>

            <Button variant="default" type="submit" isLoading={isLoading} disabled={!form.formState.isValid}>
              Continue
            </Button>
          </Stack>
        </form>
      </Form>
    </Stack>
  );
};
